/**
 * Compact job row for jobseeker dashboard home (desktop).
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faBriefcase,
  faBookmark,
  faClock,
  faDollarSign,
} from '@fortawesome/free-solid-svg-icons';

const DashboardJobRow = ({
  job,
  onApply,
  onSave,
  onViewDetails,
  isSaved = false,
  isApplied = false,
  companyName = '—',
  postedLabel = null,
  matchScore = 0,
}) => {
  const title = job.job_title || job.title || 'Untitled Role';
  const displayTitle = typeof job.displayTitle === 'string' ? job.displayTitle : title;
  const location = job.location || job.job_location || job.city || 'Not specified';
  const jobType = job.job_type || job.jobType || job.type || 'Full-time';

  const salaryRaw = job.salary_range || job.salary || job.salaryDisplay;
  const salaryLabel = typeof salaryRaw === 'string'
    ? salaryRaw
    : (salaryRaw?.min && salaryRaw?.max
      ? `${salaryRaw.currency || ''} ${salaryRaw.min}–${salaryRaw.max}`.trim()
      : null);

  const initial = companyName && companyName !== '—'
    ? companyName.charAt(0).toUpperCase()
    : 'J';

  return (
    <li
      className="dashboard-job-row"
      onClick={() => onViewDetails?.(job)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onViewDetails?.(job);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="dashboard-job-row-logo" aria-hidden="true">{initial}</div>

      <div className="dashboard-job-row-body">
        <div className="dashboard-job-row-top">
          <div className="dashboard-job-row-heading">
            <h4 className="dashboard-job-row-title">{displayTitle}</h4>
            <p className="dashboard-job-row-company">{companyName}</p>
          </div>
        </div>

        <div className="dashboard-job-row-meta">
          <span><FontAwesomeIcon icon={faMapMarkerAlt} /> {location}</span>
          <span><FontAwesomeIcon icon={faBriefcase} /> {jobType}</span>
          {salaryLabel && <span><FontAwesomeIcon icon={faDollarSign} /> {salaryLabel}</span>}
          {postedLabel && <span><FontAwesomeIcon icon={faClock} /> {postedLabel}</span>}
        </div>
      </div>

      <div className="dashboard-job-row-actions">
        {matchScore > 0 && (
          <span
            className={`dashboard-job-row-match${matchScore >= 70 ? ' is-high' : matchScore >= 40 ? ' is-mid' : ''}`}
          >
            {Math.round(matchScore)}% match
          </span>
        )}
        <button
          type="button"
          className="btn btn-primary btn-sm dashboard-job-row-apply"
          disabled={isApplied}
          onClick={async (e) => {
            e.stopPropagation();
            if (onApply && !isApplied) await onApply(job);
          }}
        >
          {isApplied ? 'Applied' : 'Apply'}
        </button>
        <button
          type="button"
          className={`btn btn-secondary btn-sm dashboard-job-row-save${isSaved ? ' is-saved' : ''}`}
          aria-label={isSaved ? 'Unsave job' : 'Save job'}
          onClick={(e) => {
            e.stopPropagation();
            onSave?.(job._id || job.id);
          }}
        >
          <FontAwesomeIcon icon={faBookmark} />
        </button>
      </div>
    </li>
  );
};

export default DashboardJobRow;
